import { useEffect, useState } from 'react';
import clsx from 'clsx';

type FontKey = 'inter' | 'geist' | 'archivo';

const FONT_STACKS: Record<FontKey, string> = {
  inter: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  geist: "'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif",
  archivo: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

const STORAGE_KEY = 'zg-font-preview';

function applyFont(key: FontKey) {
  document.documentElement.style.setProperty('--font-display', FONT_STACKS[key]);
}

/**
 * Dev-only live font toggle. Sets --font-display on :root so every Inter-
 * consuming surface reflects the choice. Persists to localStorage for session
 * continuity. Strip or hide before public launch.
 */
export function FontSwitcher() {
  const [current, setCurrent] = useState<FontKey>('inter');

  useEffect(() => {
    const stored = (localStorage.getItem(STORAGE_KEY) as FontKey | null) ?? 'inter';
    setCurrent(stored);
    applyFont(stored);
  }, []);

  const choose = (key: FontKey) => {
    setCurrent(key);
    applyFont(key);
    localStorage.setItem(STORAGE_KEY, key);
  };

  return (
    <div className="flex items-center gap-2 bg-panel border border-hairline rounded-md p-1.5 shadow-panel">
      <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle px-2">
        &gt; FONT
      </span>
      {(['inter', 'geist', 'archivo'] as const).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => choose(k)}
          className={clsx(
            'px-3 py-1.5 rounded-sm text-[12px] uppercase tracking-[0.1em] font-mono transition-colors',
            current === k
              ? 'bg-gradient-primary text-white'
              : 'text-ink-muted hover:text-ink',
          )}
        >
          {k}
        </button>
      ))}
    </div>
  );
}
